// ============================================
// pages/Home.tsx
//
// 首页 feed. 接 community-list-projects (Week 2 后端已就绪).
//
// Week 2 重做要点 (官网设计系统):
//   - 套 Layout (导航 + 底部)
//   - 顶部 hero: kicker + grad-ink 大标题 (官网风)
//   - 排序 tabs: 最新 / 热门
//   - 分类筛选 chips
//   - 游标分页 (next_cursor) + "加载更多"
//   - 响应式 grid: 1 / 2 / 3 列
//   - URL ?q= 时做前端关键词过滤兜底 (专门搜索页 Week 2 后续)
// ============================================

import { useEffect, useState, useCallback } from 'react';
import { Puzzle, WifiOff } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import ProjectCard from '../components/ProjectCard';
import LoadingSpinner from '../components/LoadingSpinner';
import { callApi } from '../services/api';
import { loadCategories, getCategoriesSync, type Category } from '../services/categories';
import type { ProjectPublic, ProjectCategory } from '../types/community';

type SortMode = 'latest' | 'hot';
type CardWithAuthor = ProjectPublic & {
  author?: { short_id: string | null; nickname: string; avatar_url: string | null; level?: number };
};

const SORTS: Array<{ id: SortMode; label: string }> = [
  { id: 'latest', label: '最新' },
  { id: 'hot', label: '热门' },
];

export default function Home() {
  const [params] = useSearchParams();
  const q = (params.get('q') || '').trim().toLowerCase();

  const [list, setList] = useState<CardWithAuthor[]>([]);
  const [sort, setSort] = useState<SortMode>('latest');
  const [category, setCategory] = useState<ProjectCategory | 'all'>('all');

  // 分类: 后端定义. 初值用 service 当前缓存 (默认兜底), 拉取完成后刷新.
  const [categories, setCategories] = useState<Category[]>(getCategoriesSync());
  useEffect(() => {
    loadCategories().then(setCategories);
  }, []);

  const [loading, setLoading] = useState(true);     // 首屏 / 切条件
  const [loadingMore, setLoadingMore] = useState(false);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [errored, setErrored] = useState(false);

  // 拉一页. reset=true 时清空重来 (切排序/分类); false 时追加 (加载更多).
  const fetchPage = useCallback(
    async (reset: boolean, cur: string | null) => {
      if (reset) setLoading(true);
      else setLoadingMore(true);
      setErrored(false);

      const r = await callApi<{ list: CardWithAuthor[]; next_cursor: string | null }>(
        'community-list-projects',
        {
          sort,
          category: category === 'all' ? null : category,
          cursor: cur,
        },
        { noAuth: true },
      );

      if (r.ok && Array.isArray(r.list)) {
        const incoming = r.list as CardWithAuthor[];
        setList((prev) => (reset ? incoming : [...prev, ...incoming]));
        setCursor((r.next_cursor as string | null) ?? null);
        setHasMore(Boolean(r.next_cursor));
      } else {
        if (reset) setList([]);
        setHasMore(false);
        // NOT_IMPLEMENTED / 集合未建 → 后端返空, 不算错; 其余才标错
        if (r.error && !['NOT_IMPLEMENTED', 'UNKNOWN_ACTION'].includes(r.error as string)) {
          setErrored(true);
        }
      }

      if (reset) setLoading(false);
      else setLoadingMore(false);
    },
    [sort, category],
  );

  // 切排序/分类 → 重拉第一页
  useEffect(() => {
    setCursor(null);
    fetchPage(true, null);
  }, [fetchPage]);

  // 前端关键词过滤兜底 (URL ?q=)
  const shown = q
    ? list.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          (p.author?.nickname || '').toLowerCase().includes(q) ||
          (p.tags || []).some((t) => t.toLowerCase().includes(q)),
      )
    : list;

  return (
    <Layout width="wide">
      {/* Studio 社区：真实硬件作品横幅 */}
      <section className="pt-6 pb-6" aria-labelledby="community-hero-title">
        <div className="community-hero">
          <div className="community-hero-copy">
            <span className="community-hero-eyebrow">HiPiiDo · 作品社区</span>
            <h1 id="community-hero-title">让编程想法，<br /><span>成为真实作品。</span></h1>
            <p>用 HiPiiDo Studio 搭积木、写代码，创造能感知、能行动的硬件作品。在这里分享过程，也从他人的创作中获得灵感。</p>
            <div className="community-hero-tags" aria-label="社区分享内容">
              <span>积木与代码</span><span>硬件与物联网</span><span>分享与学习</span>
            </div>
          </div>
          <figure className="community-hero-visual">
            <img src="/community/images/community-hardware-hero-v1.jpg" width="1536" height="1024" fetchPriority="high" alt="根据 HiPiiDo 实际作品照片加工的小车、机械臂与智能浇灌装置展示" />
            <figcaption>从 Studio 出发，连接真实世界</figcaption>
          </figure>
        </div>
      </section>

      {/* ───── 筛选条(带底色区隔) ───── */}
      <div className="sticky top-16 z-20 -mx-4 px-4 py-3 bg-white/90 backdrop-blur-md border-y border-ink-300/40 shadow-[0_2px_12px_-8px_rgba(15,23,42,0.15)]">
        <div className="flex items-center gap-3 flex-wrap">
          {/* 排序 */}
          <div className="inline-flex p-0.5 rounded-pill bg-white border border-ink-300/40">
            {SORTS.map((s) => (
              <button
                key={s.id}
                onClick={() => setSort(s.id)}
                className={`px-4 py-1.5 rounded-pill text-sm font-semibold transition-colors ${
                  sort === s.id ? 'bg-gold-500 text-white' : 'text-ink-500 hover:text-ink-900'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>

          <span className="w-px h-5 bg-ink-300/50 hidden sm:block" />

          {/* 分类 (后端定义) */}
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setCategory('all')}
              className={`px-3 py-1.5 rounded-pill text-[13px] font-medium border transition-colors ${
                category === 'all'
                  ? 'bg-gold-soft text-gold-700 border-gold-200'
                  : 'bg-white text-ink-500 border-ink-300/40 hover:border-gold-200 hover:text-gold-600'
              }`}
            >
              全部
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                onClick={() => setCategory(c.id as ProjectCategory)}
                className={`px-3 py-1.5 rounded-pill text-[13px] font-medium border transition-colors ${
                  category === c.id
                    ? 'bg-gold-soft text-gold-700 border-gold-200'
                    : 'bg-white text-ink-500 border-ink-300/40 hover:border-gold-200 hover:text-gold-600'
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ───── 内容 ───── */}
      <div className="py-8 min-h-[40vh]">
        {q && (
          <p className="text-sm text-ink-500 mb-5">
            关键词「<span className="text-gold-700 font-medium">{q}</span>」的结果 ·{' '}
            {shown.length} 个
          </p>
        )}

        {loading ? (
          <div className="py-20 flex justify-center">
            <LoadingSpinner />
          </div>
        ) : errored ? (
          <EmptyState
            icon={<WifiOff className="w-12 h-12" strokeWidth={1.5} />}
            title="加载失败"
            desc="创新工场数据暂时拉取不到,请稍后刷新重试。"
          />
        ) : shown.length === 0 ? (
          <EmptyState
            icon={<Puzzle className="w-12 h-12" strokeWidth={1.5} />}
            title={q ? '没有匹配的作品' : '还没有作品'}
            desc={q ? '换个关键词试试。' : '回到 HiPiiDo Studio,点「分享」发布第一个作品吧!'}
          />
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {shown.map((p) => (
                <ProjectCard key={p.short_id} project={p} showAuthor />
              ))}
            </div>

            {/* 加载更多 (有关键词过滤时不分页, 走前端过滤当前已加载集) */}
            {!q && hasMore && (
              <div className="flex justify-center mt-10">
                <button
                  className="btn-secondary"
                  disabled={loadingMore}
                  onClick={() => fetchPage(false, cursor)}
                >
                  {loadingMore ? '加载中…' : '加载更多'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}

function EmptyState({ icon, title, desc }: { icon: React.ReactNode; title: string; desc: string }) {
  return (
    <div className="bg-white rounded-2xl border border-ink-300/40 py-16 px-6 text-center shadow-card">
      <div className="flex justify-center mb-4 text-gold-300">{icon}</div>
      <h2 className="text-lg font-bold text-ink-900 mb-2">{title}</h2>
      <p className="text-ink-500 text-sm max-w-sm mx-auto leading-relaxed">{desc}</p>
    </div>
  );
}
